import React, { useState, useEffect } from "react";
import { AlertCircle, AlertTriangle, Info, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import freightService from "@/services/freightService";

const EMPTY_FORM = {
  minDistanceKm: "",
  maxDistanceKm: "",
  freightValue: "",
};

/** Número da API → texto editável em pt-BR (5.5 → "5,5") */
const toInput = (value, decimals) => {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  return (decimals ? number.toFixed(decimals) : String(number)).replace(".", ",");
};

const FieldError = ({ id, message }) =>
  message ? (
    <p id={id} className="flex items-center gap-1 text-xs text-destructive">
      <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
      {message}
    </p>
  ) : null;

/**
 * Modal "Nova / Editar Taxa de Frete" (docs/tela/code.html).
 *
 * Props: `open`, `onOpenChange`, `rate` (null = criação), `rates` (taxas
 * ativas, para sugerir a próxima faixa e avisar sobreposição), `onSave`,
 * `loading`.
 */
export default function FreightRateFormModal({
  open,
  onOpenChange,
  rate,
  rates = [],
  onSave,
  loading,
}) {
  const isEditing = !!rate;

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    if (rate) {
      setFormData({
        minDistanceKm: toInput(rate.minDistanceKm),
        maxDistanceKm: toInput(rate.maxDistanceKm),
        freightValue: toInput(rate.freightValue, 2),
      });
    } else {
      // Sugere começar onde termina a maior faixa cadastrada
      const lastMax = freightService.getSummary(rates).coverage;
      setFormData({ ...EMPTY_FORM, minDistanceKm: toInput(lastMax) });
    }
    setErrors({});
    // Só reinicia ao abrir ou trocar a taxa editada (rates fica de fora de propósito)
  }, [rate, open]);

  const handleChange = (field, value) => {
    // Aceita só dígitos e um separador decimal
    const clean = value.replace(/[^\d.,]/g, "");
    setFormData((prev) => ({ ...prev, [field]: clean }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const min = freightService.parseNumber(formData.minDistanceKm);
  const max = freightService.parseNumber(formData.maxDistanceKm);
  const value = freightService.parseNumber(formData.freightValue);
  const hasValidRange = Number.isFinite(min) && Number.isFinite(max) && max > min;

  const overlaps = hasValidRange
    ? freightService.findOverlaps(
        { id: rate?.id, minDistanceKm: min, maxDistanceKm: max },
        rates,
      )
    : [];

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = freightService.validateRateData(formData);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    onSave({
      minDistanceKm: min,
      maxDistanceKm: max,
      freightValue: Math.round(value * 100) / 100,
      active: true,
    });
  };

  const inputClass = (field) =>
    cn(
      "h-11 w-full rounded-lg border border-transparent bg-surface px-3.5 text-sm text-foreground transition-colors placeholder:text-muted-foreground/80 focus:border-primary focus:bg-card focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25 disabled:opacity-70",
      errors[field] && "border-destructive",
    );

  const distanceField = (field, label, placeholder) => (
    <div className="space-y-1.5">
      <Label htmlFor={`freight-${field}`} className="text-[13px] font-semibold">
        {label} <span className="text-destructive">*</span>
      </Label>
      <input
        id={`freight-${field}`}
        inputMode="decimal"
        autoComplete="off"
        placeholder={placeholder}
        value={formData[field]}
        onChange={(e) => handleChange(field, e.target.value)}
        className={inputClass(field)}
        disabled={loading}
        aria-invalid={Boolean(errors[field])}
        aria-describedby={errors[field] ? `freight-${field}-error` : undefined}
      />
      <FieldError id={`freight-${field}-error`} message={errors[field]} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <DialogContent className="max-h-[90vh] max-w-md gap-4 overflow-y-auto rounded-2xl p-5 md:p-6">
        <DialogHeader className="pr-8 text-left">
          <DialogTitle className="font-display text-lg text-foreground">
            {isEditing ? "Editar Taxa de Frete" : "Nova Taxa de Frete"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            A taxa vale para entregas com distância a partir da mínima e abaixo
            da máxima.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3" noValidate>
          <div className="grid grid-cols-2 gap-3">
            {distanceField("minDistanceKm", "Distância mínima (km)", "0")}
            {distanceField("maxDistanceKm", "Distância máxima (km)", "5")}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="freight-freightValue" className="text-[13px] font-semibold">
              Valor do frete (R$) <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[13px] font-semibold text-muted-foreground">
                R$
              </span>
              <input
                id="freight-freightValue"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0,00"
                value={formData.freightValue}
                onChange={(e) => handleChange("freightValue", e.target.value)}
                className={cn(inputClass("freightValue"), "pl-10")}
                disabled={loading}
                aria-invalid={Boolean(errors.freightValue)}
                aria-describedby={
                  errors.freightValue ? "freight-freightValue-error" : undefined
                }
              />
            </div>
            <FieldError id="freight-freightValue-error" message={errors.freightValue} />
          </div>

          {/* Resumo da regra, atualizado enquanto digita */}
          <div className="flex items-start gap-2 rounded-lg bg-surface p-3 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            {hasValidRange ? (
              <span>
                Entregas de{" "}
                <strong className="text-foreground">
                  {freightService.formatDistance(min)}
                </strong>{" "}
                até menos de{" "}
                <strong className="text-foreground">
                  {freightService.formatDistance(max)}
                </strong>{" "}
                pagarão{" "}
                <strong className="text-primary">
                  {freightService.formatCurrency(Number.isFinite(value) ? value : 0)}
                </strong>
                .
              </span>
            ) : (
              <span>
                Informe uma distância máxima maior que a mínima para ver o
                resumo da faixa.
              </span>
            )}
          </div>

          {overlaps.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg bg-terra-soft p-3 text-xs text-terra">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>
                Esta faixa se sobrepõe a{" "}
                {overlaps.map((r) => freightService.formatRange(r)).join(", ")}. O
                cálculo pode usar qualquer uma das faixas sobrepostas.
              </span>
            </div>
          )}

          <div className="flex items-center justify-end gap-2.5 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="h-10 rounded-full bg-surface-container px-5 text-[13px] font-semibold text-foreground transition-colors hover:bg-surface-high disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-primary px-6 text-[13px] font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95 disabled:opacity-70"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Salvando..." : isEditing ? "Atualizar" : "Criar Taxa"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
